import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ActionCard from './ActionCard';
import { HistoryService } from '../services/HistoryService';
import { NotificationService } from '../services/NotificationService';
import { CognitiveVerifier } from '../services/CognitiveVerifier';
import { apiRequest } from '../services/apiClient';

const DashboardPage = () => {
  const { user, accessToken, logout } = useAuth();
  
  // Normalize initial user state combining context and local storage overrides
  const [liveProfile, setLiveProfile] = useState(() => {
    return user || JSON.parse(localStorage.getItem('user') || '{}');
  });

  const [academicHistory, setAcademicHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!accessToken) {
        // Fallback sync using current user context if token is absent in standalone mode
        if (user) setLiveProfile(user);
        
        // Populate fallback mock local assignments and local history cleanly when running locally unauthenticated
        const localAssignments = JSON.parse(localStorage.getItem('llap_teacher_assignments') || '[]');
        const assignmentNotifs = localAssignments.map((a, idx) => ({
          _id: `assignment-${idx}`,
          title: `New Assignment: ${a.title}`,
          message: `Module: ${a.module} | Due by: ${new Date(a.dueDate).toLocaleString()}`,
          readStatus: false
        }));
        setNotifications(assignmentNotifs);
        setUnreadCount(assignmentNotifs.length);
        
        const localHistory = JSON.parse(localStorage.getItem('llap_academic_history') || '[]');
        setAcademicHistory(localHistory);
        return;
      }

      setLoadingData(true);
      try {
        // Defensively parse API payloads using apiRequest wrapper with safe catch handling
        const [profileRes, historyRes, notifRes] = await Promise.all([
          apiRequest('get-profile', { method: 'GET' }).catch(err => ({ success: false, fallback: true, error: err })),
          HistoryService.getAcademicHistory(accessToken).catch(err => ({ success: true, history: JSON.parse(localStorage.getItem('llap_academic_history') || '[]') })),
          NotificationService.getNotifications(accessToken).catch(err => ({ success: true, notifications: [] }))
        ]);

        if (profileRes && profileRes.success && profileRes.profile) {
          const merged = { ...user, ...profileRes.profile };
          if (!merged.studentClassification && user?.studentClassification) {
            merged.studentClassification = user.studentClassification;
          }
          setLiveProfile(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        } else if (user) {
          setLiveProfile(user);
        }

        const historyList = (historyRes && historyRes.success && Array.isArray(historyRes.history)) 
          ? historyRes.history 
          : JSON.parse(localStorage.getItem('llap_academic_history') || '[]');
        setAcademicHistory(historyList);

        // Merge scheduled assignments into notification feed cleanly
        const localAssignments = JSON.parse(localStorage.getItem('llap_teacher_assignments') || '[]');
        const assignmentNotifs = localAssignments.map((a, idx) => ({
          _id: `assignment-${idx}`,
          title: `New Assignment: ${a.title}`,
          message: `Module: ${a.module} | Due by: ${new Date(a.dueDate).toLocaleString()}`,
          readStatus: false
        }));

        const remoteNotifs = (notifRes && notifRes.success && Array.isArray(notifRes.notifications)) ? notifRes.notifications : [];
        const combinedNotifs = [...assignmentNotifs, ...remoteNotifs];

        setNotifications(combinedNotifs);
        setUnreadCount(combinedNotifs.filter(n => !n.readStatus).length);
      } catch (err) {
        console.warn('Dashboard data sync notice (using local fallbacks):', err.message);
        if (user) setLiveProfile(user);
        
        const localAssignments = JSON.parse(localStorage.getItem('llap_teacher_assignments') || '[]');
        const assignmentNotifs = localAssignments.map((a, idx) => ({
          _id: `assignment-${idx}`,
          title: `New Assignment: ${a.title}`,
          message: `Module: ${a.module} | Due by: ${new Date(a.dueDate).toLocaleString()}`,
          readStatus: false
        }));
        setNotifications(assignmentNotifs);
        setUnreadCount(assignmentNotifs.length);

        const localHistory = JSON.parse(localStorage.getItem('llap_academic_history') || '[]');
        setAcademicHistory(localHistory);
      } finally {
        setLoadingData(false);
      }
    };

    fetchDashboardData();
  }, [accessToken, user]);

  const handleMarkAsRead = async (id) => {
    try {
      await NotificationService.markAsRead(id, accessToken);
      setNotifications(prev => prev.map(n => (n._id === id || n.id === id) ? { ...n, readStatus: true, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      setNotifications(prev => prev.map(n => (n._id === id || n.id === id) ? { ...n, readStatus: true, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await NotificationService.markAllAsRead(accessToken);
      setNotifications(prev => prev.map(n => ({ ...n, readStatus: true, read: true })));
      setUnreadCount(0);
    } catch (err) {
      setNotifications(prev => prev.map(n => ({ ...n, readStatus: true, read: true })));
      setUnreadCount(0);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loadingData && !liveProfile) {
    return (
      <div role="status" aria-live="polite" style={{ padding: '60px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
        <h2 style={{ color: '#0056b3' }}>Loading your LLAP learning dashboard...</h2>
        <p style={{ color: '#666' }}>Synchronizing authenticated session and live profile metadata.</p>
      </div>
    );
  }

  // Robust verification combining live profile, session user, and explicit string checking
  const activeUser = liveProfile || user || {};
  const classification = (activeUser.studentClassification || activeUser.studentType || '').toUpperCase();
  const isInstructor = classification === 'INSTRUCTOR' || CognitiveVerifier.isInstructorStudent(activeUser);
  const permissions = CognitiveVerifier.getNormalizedPermissions(activeUser);

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      {/* Top Header with Live Student Greeting & Logout */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #eaeaea', paddingBottom: '20px', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#0056b3', margin: '0 0 5px 0' }}>
            LLAP Academy — {isInstructor ? 'Instructor Student Hub' : 'Public Explorer Hub'}
          </h1>
          <p style={{ color: '#666', margin: 0 }}>
            Welcome back, {activeUser?.fullName || 'Student'}! ({isInstructor ? 'INSTRUCTOR STUDENT' : 'PUBLIC'})
          </p>
        </div>
        <button 
          onClick={handleLogout}
          style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
        >
          Logout
        </button>
      </header>

      {/* Notice Banner: Strict conditional rendering separating Instructor Student Mode from Public Explorer Mode */}
      {isInstructor ? (
        <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', padding: '15px', borderRadius: '6px', marginBottom: '30px', color: '#155724' }}>
          <strong>Notice:</strong> You are using LLAP Instructor Student Mode. Your account provides access to assigned assessments, academic progress tracking, teacher-reviewed results, feedback, and eligible certificates. Your academic results will become available when released by your assigned teacher.
        </div>
      ) : (
        <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: '15px', borderRadius: '6px', marginBottom: '30px', color: '#856404' }}>
          <strong>Notice:</strong> You are exploring LLAP in <strong>Public Explorer Mode</strong>. Mock exam submissions require teacher review and release. Upgrade to an Instructor Student membership for full academic analytics and certificates.
        </div>
      )}

      {/* Database-Driven Profile & Dynamic Notification Center Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        {/* Profile Card */}
        <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>Student Profile</h3>
          <p><strong>Email:</strong> {activeUser?.email || 'N/A'}</p>
          <p>
            <strong>Classification:</strong>{' '}
            <span style={{ background: isInstructor ? '#d4edda' : '#e2e3e5', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
              {isInstructor ? 'INSTRUCTOR' : 'PUBLIC'}
            </span>
          </p>
          <p><strong>Programme:</strong> {activeUser?.programme || 'IELTS Academic Preparation'}</p>
          <p><strong>Account Status:</strong> {activeUser?.accountStatus || (activeUser?.emailVerified ? '✅ Verified' : '⚠️ Unverified')}</p>
          <p><strong>Member Since:</strong> {activeUser?.createdAt ? new Date(activeUser.createdAt).toLocaleDateString() : 'N/A'}</p>
        </div>

        {/* Dynamic Notification Center */}
        <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', maxHeight: '300px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, color: '#333' }}>Notifications ({unreadCount} unread)</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p style={{ color: '#666', fontSize: '14px' }}>No notifications found.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {notifications.map((n, idx) => {
                const notifId = n._id || n.id || idx;
                const isRead = n.readStatus || n.read;
                return (
                  <li key={notifId} style={{ padding: '10px 0', borderBottom: '1px solid #eee', background: isRead ? 'transparent' : '#eef6fc', borderRadius: '4px', marginBottom: '5px', paddingLeft: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong style={{ fontSize: '14px', color: '#333' }}>{n.title}</strong>
                      {!isRead && (
                        <button onClick={() => handleMarkAsRead(notifId)} style={{ background: '#0056b3', color: '#fff', border: 'none', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer' }}>
                          Mark Read
                        </button>
                      )}
                    </div>
                    <p style={{ margin: '4px 0', fontSize: '13px', color: '#555' }}>{n.message}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Academic History */}
      {permissions.canViewResults ? (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '40px' }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>Academic History & Module Results</h3>
          {academicHistory.length === 0 ? (
            <p style={{ color: '#666' }}>No completed assessments recorded yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f1f1', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '10px' }}>Module</th>
                    <th style={{ padding: '10px' }}>Assessment Name</th>
                    <th style={{ padding: '10px' }}>Score</th>
                    <th style={{ padding: '10px' }}>Estimated Band</th>
                    <th style={{ padding: '10px' }}>Status</th>
                    <th style={{ padding: '10px' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {academicHistory.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px' }}>{item.module || 'Reading'}</td>
                      <td style={{ padding: '10px' }}>{item.assessmentName || 'Mock Assessment'}</td>
                      <td style={{ padding: '10px' }}>{item.rawScore !== undefined ? item.rawScore : 'N/A'}</td>
                      <td style={{ padding: '10px' }}>{item.estimatedBand || 'N/A'}</td>
                      <td style={{ padding: '10px' }}>{item.completionStatus || item.status || 'Completed'}</td>
                      <td style={{ padding: '10px' }}>{item.attemptedAt ? new Date(item.attemptedAt).toLocaleDateString() : new Date().toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '40px', textAlign: 'center' }}>
          <h3 style={{ color: '#666' }}>Academic Results & Certificates</h3>
          <p style={{ color: '#777' }}>Detailed scores and certificates are available exclusively for Instructor Students upon teacher release.</p>
        </div>
      )}

      <hr style={{ margin: '30px 0', border: '0', borderTop: '1px solid #eaeaea' }} />

      {/* Programme Selection Action Cards */}
      <h2 style={{ color: '#333', marginBottom: '20px' }}>Choose Your Programme</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        <ActionCard title="IELTS" color="#007bff" icon="📖" onClick={() => navigate('/ielts')} />
        <ActionCard title="Cambridge IG" color="#28a745" icon="🎓" onClick={() => navigate('/cambridge')} />
        <ActionCard title="Linguaskill" color="#fd7e14" icon="✍️" status="coming-soon" />
      </div>
    </div>
  );
};

export default DashboardPage;